<template>
  <div class="flex">
    <AddToCartInputGroup
      v-if="isLoggedIn && isPurchasable.online && isPurchasable.store"
      data-test="add-to-cart-input-group"
      class="w-auto"
      :class="'add-to-cart ' + layout"
      :size="size"
      :stretch="stretch"
      :order-multiple="orderMultiple"
      :initial-quantity="initialQuantityValue"
      :unit-conversion-factor="Number(getQuantityPerSalesUnit(variant))"
      :sales-unit-label="getSalesUnitLabel"
      :sales-price-unit-label="getSalesPriceUnitLabel"
      :button-shape="buttonShape"
      :buy-disabled="!cartId"
      @click="addToCart($event)"
      @quantity-update="onQuantityUpdate"
    >
    </AddToCartInputGroup>
    <LazyOrderRequestButton
      v-else
      data-test="order-request-button"
      :variant="variant"
      :size="size"
      :shape="buttonShape"
    />
    <LazyFavoriteToggle v-if="isFavoritesIconVisible" :article-number="variant.articleNumber" class="mx-2" />
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { useCartStore, useUserStore, useStoresStore, useDeliveryStore } from '@/stores';
import type { CButtonShape, CButtonSize } from '@/types/ui';
import type { Variant } from '@/types/products';

type Props = {
  variant?: Variant;
  size?: CButtonSize;
  stretch?: boolean;
  label?: string;
  commonVariantAttributes?: any[];
  buttonShape?: CButtonShape;
  isFavoritesIconVisible?: boolean;
  initialQuantity?: number;
  layout?: string;
  deliveryId?: string;
};

const props = withDefaults(defineProps<Props>(), {
  variant: undefined,
  size: 'medium',
  stretch: false,
  label: '',
  commonVariantAttributes: () => [],
  buttonShape: 'squared',
  isFavoritesIconVisible: false,
  initialQuantity: 1,
  layout: '',
  deliveryId: '',
});

const emit = defineEmits<{
  (e: 'quantity-update', quantity: number): void;
}>();

const { cartId } = storeToRefs(useCartStore());
const { isLoggedIn } = storeToRefs(useUserStore());
const { getStoreIdForSelectedDeliveryId } = storeToRefs(useStoresStore());
const { getCurrentStockLocation, selectedLocations } = storeToRefs(useDeliveryStore());
const { addItemToCart } = useCartStore();

const trackingPosition = inject('trackingPosition', 'unknown_list');
const injectedOrderMultiple = inject(
  'orderMultiple',
  computed(() => null),
);
const injectedIsPurchasable = inject(
  'isPurchasable',
  computed(() => null),
);

const orderMultiple = computed(
  () => injectedOrderMultiple.value ?? getOrderMultiple(getStoreIdForSelectedDeliveryId.value, props.variant.stock),
);

const initialQuantityValue = computed(() =>
  props.initialQuantity > orderMultiple.value ? props.initialQuantity : orderMultiple.value,
);

const isPurchasable = computed(
  () =>
    injectedIsPurchasable.value ??
    isPurchasableOnline(
      props.variant,
      props.deliveryId ? selectedLocations.value[props.deliveryId] : getCurrentStockLocation.value,
    ),
);

const getSalesUnitLabel = computed(() =>
  props.variant?.webSalesUnit ? props.variant.webSalesUnit?.conversionUnit : undefined,
);

const getSalesPriceUnitLabel = computed(
  () => props.variant?.webSalesUnit?.unit || props.variant?.price?.salesPriceUnit,
);

const getQuantityPerSalesUnit = (variant: Variant) => {
  // Use webSalesUnits for new PIM
  const quantityPerSalesUnit = variant.webSalesUnit ? variant.webSalesUnit?.conversionFactor : undefined;
  const quantityPerM2Common = props.commonVariantAttributes.find(
    // Use "quantityPerM2" for old PIM
    (attribute) => attribute.attributeName === 'quantityPerM2',
  );

  const m2Value = quantityPerM2Common?.attributeValue
    ? replaceCommaWithPeriod(quantityPerM2Common.attributeValue.toString())
    : null;
  return quantityPerSalesUnit || m2Value;
};

const addToCart = (quantity: number) => {
  const payload = { ...props.variant, quantity };
  if (props.label) payload.label = props.label;
  payload.filterTags = props.variant.filterTags || [];
  addItemToCart(payload, trackingPosition);
};

const onQuantityUpdate = (quantity: number) => {
  emit('quantity-update', quantity);
};
</script>

<script lang="ts">
export default defineComponent({
  name: 'AddToCart',
});
</script>
